/**
 * What each figure on a report means, written down where the code can read it.
 *
 * ## Why this is data and not prose in a component
 *
 * A financial metric whose definition lives only in a tooltip is a metric
 * nobody can check. Two people will read "overdue" as two different things —
 * money past its due date, or an account whose follow-up date has gone by —
 * and the product will quietly mean one of them. These definitions are the
 * answer to "what exactly is this number", they are shown on the screen, and
 * `docs/saas-launch/18-REPORT-METRICS.md` is generated from the same list.
 *
 * ## The distinction that matters most
 *
 * Every metric is one of three kinds, and mixing them is how a report claims to
 * know something the product cannot:
 *
 *   * **current** — what is true right now, from the latest sync. It has no
 *     period and does not change when somebody picks a different month.
 *   * **period** — something that happened between two dates, read from a
 *     timestamped, immutable record.
 *   * **unsupported** — listed here deliberately, with what the product would
 *     have to start storing first. These are not built.
 *
 * The one that is always asked for and is always in the third category is
 * **money collected**. Settling an account zeroes its balance; the amount it
 * used to carry is not kept, so the sum that arrived cannot be reconstructed.
 * `settled_at` records when a *sync first saw* a balance clear — a weaker fact,
 * reported with that wording and never as money.
 */

export type MetricKind = 'current' | 'period' | 'unsupported';

export interface MetricDefinition {
    key: string;
    label: string;
    kind: MetricKind;
    /** What the number is, in one sentence somebody can check against. */
    definition: string;
    /** Where it comes from, for anybody auditing it. */
    source: string;
    /** Who may see it, beyond the universal visibility rules. */
    permission: string;
    /** For `unsupported`: what would have to be stored first. */
    missing?: string;
}

/**
 * Every figure the reports show, and three that they deliberately do not.
 *
 * All of them are additionally bounded by the rules that apply to everything in
 * this product: the active workspace, an active membership, and
 * `can_see_customer()`. A collector's report is of their own accounts, and the
 * aggregate is as much of a disclosure as the rows, so nothing here is computed
 * anywhere but in the database as the caller.
 */
export const REPORT_METRICS: readonly MetricDefinition[] = [
    /* ------------------------------ current ------------------------------ */
    {
        key: 'outstanding',
        label: 'Outstanding',
        kind: 'current',
        definition:
            'What the company is owed on balance: every account with a non-zero balance, '
            + 'debits positive and credits negative, added together.',
        source: 'customers.total and total_type, from the latest sync',
        permission: 'Any member; the figure covers the accounts the reader may see',
    },
    {
        key: 'receivable',
        label: 'Owed to you',
        kind: 'current',
        definition: 'The debit side alone — what customers owe, before any credit is netted off.',
        source: 'customers.total where the direction is Debit',
        permission: 'Any member; the figure covers the accounts the reader may see',
    },
    {
        key: 'credit',
        label: 'Held for customers',
        kind: 'current',
        definition:
            'Money sitting with the company that belongs to a customer — advances and credit '
            + 'notes. Shown separately and never netted into the receivable without saying so.',
        source: 'customers.total where the direction is Credit',
        permission: 'Any member; the figure covers the accounts the reader may see',
    },
    {
        key: 'overdue',
        label: 'Overdue follow-ups',
        kind: 'current',
        definition:
            'Accounts with a balance whose **follow-up date** has gone by. This is about the '
            + 'promise to call, not about the age of the money — the ageing table answers that.',
        source: 'book_follow_up_category(), from customers.follow_up_date in the company timezone',
        permission: 'Any member; covers the accounts the reader may see',
    },
    {
        key: 'ageing_bucket',
        label: 'Ageing buckets',
        kind: 'current',
        definition:
            'Debit still outstanding in each age band, net of any credit in the same band, '
            + 'honouring the sheet’s own roll-ups where it supplies them. An account in credit '
            + 'overall has nothing overdue.',
        source: 'book_overdue() — the same function the Book’s ageing filter uses',
        permission: 'Any member; covers the accounts the reader may see',
    },
    {
        key: 'promised',
        label: 'Promised',
        kind: 'current',
        definition:
            'What customers have committed to pay, on open accounts. A promise, not a receipt: '
            + 'it is what somebody said on a call and it may not arrive.',
        source: 'customers.forecast_amount where the account is not marked collected',
        permission: 'Any member; covers the accounts the reader may see',
    },
    {
        key: 'bad_debt',
        label: 'Written off',
        kind: 'current',
        definition:
            'Accounts somebody has declared a defaulter by hand. Never inferred from ageing — '
            + 'being a defaulter is a decision about a customer, not a reading of a date.',
        source: 'customers.payment_rank = Bad',
        permission: 'Any member; covers the accounts the reader may see',
    },
    {
        key: 'cheques_in_hand',
        label: 'Cheques in hand',
        kind: 'current',
        definition:
            'Cheques the company is holding that the bank has not acted on: upcoming, due today, '
            + 'date passed, and on hold.',
        source: 'cheque_state() over pdc_cheques',
        permission: 'Needs cheques.view; covers cheques on accounts the reader may see',
    },

    /* ------------------------------ period ------------------------------- */
    {
        key: 'follow_ups',
        label: 'Follow-ups recorded',
        kind: 'period',
        definition:
            'Entries people wrote in customer histories during the period. The importer’s own '
            + 'entries are excluded — they are not somebody’s work.',
        source: 'customer_activity, by created_at in the company timezone, excluding kind = system',
        permission: 'Any member; covers the accounts the reader may see',
    },
    {
        key: 'accounts_touched',
        label: 'Accounts contacted',
        kind: 'period',
        definition:
            'Distinct customers with at least one entry in the period. Ten calls to one customer '
            + 'is one account contacted — counting it as ten would make a difficult account look '
            + 'like a productive day.',
        source: 'distinct customer_id in customer_activity over the period',
        permission: 'Any member; covers the accounts the reader may see',
    },
    {
        key: 'promises_made',
        label: 'Promises taken',
        kind: 'period',
        definition: 'Promise entries recorded during the period, and what they came to.',
        source: 'customer_activity where kind = promise, with promised_amount',
        permission: 'Any member; covers the accounts the reader may see',
    },
    {
        key: 'cheques_cleared',
        label: 'Cheques cleared',
        kind: 'period',
        definition:
            'Cheques marked cleared during the period, and their value. The closest thing to a '
            + 'collection figure this product honestly has — and it is cheques, not collections. '
            + 'A cheque cleared and later reopened keeps only its latest date.',
        source: 'pdc_cheques.cleared_date, written when somebody marks a cheque cleared',
        permission: 'Needs cheques.view; covers cheques on accounts the reader may see',
    },
    {
        key: 'accounts_settled',
        label: 'Accounts settled',
        kind: 'period',
        definition:
            'Accounts whose balance a sync first saw clear during the period. **Not** when the '
            + 'money arrived, and not an amount: it is the day the sheet stopped showing a '
            + 'balance, which may be days after payment.',
        source: 'customers.settled_at, written by apply_sync',
        permission: 'Any member; covers the accounts the reader may see',
    },

    /* --------------------------- not supported --------------------------- */
    {
        key: 'amount_collected',
        label: 'Amount collected',
        kind: 'unsupported',
        definition:
            'How much money came in during a period. Asked for constantly and not built, because '
            + 'the product would have to invent it.',
        source: '—',
        permission: '—',
        missing:
            'Settling an account zeroes its balance and the previous figure is not kept, so the '
            + 'amount that arrived is unrecoverable. It would need a payments record written at '
            + 'the moment a balance falls, or a per-sync balance history.',
    },
    {
        key: 'ageing_history',
        label: 'Ageing at a past date',
        kind: 'unsupported',
        definition: 'What the ageing profile looked like last month.',
        source: '—',
        permission: '—',
        missing:
            'Each sync overwrites the ageing columns. It would need a snapshot per sync — cheap '
            + 'to store and a genuine product opportunity, but it is not stored today and the '
            + 'figures cannot be reconstructed backwards.',
    },
    {
        key: 'bounced_in_period',
        label: 'Cheques bounced during a period',
        kind: 'unsupported',
        definition: 'How many cheques came back unpaid this month.',
        source: '—',
        permission: '—',
        missing:
            'A bounce sets a status, not a timestamp — only "currently bounced" is knowable. It '
            + 'would need a dated status-change record, which the customer history already holds '
            + 'as prose but not as data.',
    },
];

export const metricDefinition = (key: string): MetricDefinition | undefined =>
    REPORT_METRICS.find(m => m.key === key);

export const metricsOfKind = (kind: MetricKind): MetricDefinition[] =>
    REPORT_METRICS.filter(m => m.kind === kind);

/** Said on screen beside a section, so nobody has to guess which kind it is. */
export const KIND_CAPTION: Record<Exclude<MetricKind, 'unsupported'>, string> = {
    current: 'As things stand now — these do not change when you change the period.',
    period: 'What happened in the period you chose.',
};
